import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Navigation History Service
 *
 * Tracks in-app navigation so a component's "Back" button can follow the REAL
 * trail (`Location.back()`) instead of a hardcoded route — e.g. opening a
 * workspace email from the Search screen and pressing Back should return to
 * Search, not jump to the email's basket.
 *
 * Instantiated at bootstrap (injected by AppComponent) so it counts every
 * NavigationEnd from app start — `canGoBack()` is therefore false on the very
 * first page (a deep link / fresh load), where `Location.back()` would leave
 * the app and a caller should fall back to an explicit route.
 */
@Injectable({ providedIn: 'root' })
export class NavigationHistoryService {

    private navigationCount = 0;
    private _previousUrl: string | null = null;
    private _currentUrl: string | null = null;

    constructor(router: Router) {
        router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe((e) => {
                this.navigationCount++;
                this._previousUrl = this._currentUrl;
                this._currentUrl = e.urlAfterRedirects;
            });
    }

    /** True when there is an earlier in-app page to return to this session. */
    canGoBack(): boolean {
        return this.navigationCount > 1;
    }

    /** The URL the user was on before the current one (null on the first page). */
    get previousUrl(): string | null {
        return this._previousUrl;
    }
}
